import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface CurrentPercentileRankingsProps {
  data: {
    worldPercentile: number | null;
    continentPercentile: number | null;
    countryPercentile: number | null;
    period: string | null;
  } | null;
  loading?: boolean;
  title?: string;
}

export const CurrentPercentileRankings: React.FC<CurrentPercentileRankingsProps> = ({
  data,
  loading = false,
  title = 'Current Market Percentile Rankings',
}) => {
  const theme = useTheme();

  // Colors for the three ranking levels
  const colors = {
    world: '#003f9f',       // Blue for world
    continent: '#16d9bc',   // Teal for continent
    country: '#0c7876',     // Dark teal for country
  };

  // Prepare chart data
  const chartData = React.useMemo(() => {
    if (!data) return [];

    return [
      {
        name: 'World',
        value: data.worldPercentile ?? 0,
        color: colors.world,
      },
      {
        name: 'Continent',
        value: data.continentPercentile ?? 0,
        color: colors.continent,
      },
      {
        name: 'Country',
        value: data.countryPercentile ?? 0,
        color: colors.country,
      },
    ];
  }, [data]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {payload[0].payload.name}
          </Typography>
          <Typography variant="body2" color="primary">
            Percentile: {payload[0].value.toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Higher values indicate better performance relative to peers
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={350} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (!data || chartData.every(item => item.value === 0)) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            {title}
          </Typography>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No ranking data available for this market
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          {title}
        </Typography>

        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 50, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
                label={{
                  value: 'Percentile Ranking',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12, textAnchor: 'middle' },
                }}
              />
              <Tooltip content={<CustomTooltip />} />

              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Percentile rankings show this market's performance relative to all markets (0-100 scale)
        </Typography>
        {data?.period && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Data as of: {new Date(data.period).toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric'
            })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
